import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { CSSObject } from '@emotion/styled';
import { getBackgroundTheme, getBorders, getButtonTheme, getColor, getTextColor } from '../../../style';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useMixPanel from '../../../hooks/useMixPanel';
import { GENERAL_MESSAGE_AVATAR, GENERAL_MESSAGE_USERNAME } from '../../../config/Master';
import { renderAvatar } from '../../../common';
import ArrowLeft from '../../../atoms/icons/ArrowLeft';
import Avatar from '../../../atoms/Avatar';
import { commonIcons } from '../../../atoms/icons/materialIcons';
import VideoCallModal from '../../modals/VideoCallModal';
import Button from '../../../atoms/buttons/Button';
import DotMenu from '../../../atoms/icons/DotMenu';
import { MenuList, MenuListItem } from '../../../components/blocks/MenuList';
import ConfirmationModal from '../../modals/ConfirmationModal';
import { RootState } from '../../../store';
import breakpoints from '../../../theme/material/breakpoints';
import { closeConversation, handleCancelOffer, setblockUser, setToggleArchive } from '../../../actions/mailActions';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { useDisableBodyElementScroll } from '../../../hooks/useDisableBodyScroll';
import { isEmpty } from 'lodash';
import { getReportReasons } from '../../../actions/profileActions';
import ReportModal from '../../../components/report/ReportModal';
import { CustomStyledProps } from 'style/style';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { isDarkMode, IsMobileViewPort } from 'utils/helpers';

interface IConversationHeader {
  dataTestId?: string;
  isConversationSevered?: boolean;
  settings?: {
    [key: string]: boolean;
  };
}

const StyledConversationHeader = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    [breakpoints.down('sm')]: {
      padding: '7.5px 10px',
    },
    [breakpoints.down(768.05)]: {
      flexDirection: 'column',
      padding: 0,
      border: getBorders().PRIMARY,
      borderRadius: 10,
      background: isDarkMode() ? '#212121' : '#fff',
    },
  }),
  ({ isConversationSevered }: { isConversationSevered?: boolean }) => ({
    [breakpoints.down(768.05)]: {
      border: isConversationSevered ? 'none !important' : getBorders().PRIMARY,
    },
  })
);

const ConversationHeaderBackBtnWrapper = styled('div')(
  (): CSSObject => ({
    display: 'none',
    [breakpoints.down(768.05)]: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: getBorders().PRIMARY,
      background: isDarkMode()
        ? 'linear-gradient(180deg, #2C5456 0%, #212121 100%)'
        : 'linear-gradient(180deg, #E6F6F7 0%, #FFF 100%)',
      padding: '20px 15px',
      gap: 10,
      borderTopLeftRadius: '10px',
      borderTopRightRadius: '10px',
    },
  })
);
const ConversationHeaderUserInfoWrapper = styled('div')(
  (): CSSObject => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 20px 10px 30px',
    [breakpoints.down(768.05)]: {
      width: 'auto',
      background: getBackgroundTheme().block2,
      borderRadius: '0px 0px 10px 10px',
      padding: 10,
    },
  })
);

const UserInfo = styled('div')({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',

  '> div': {
    display: 'flex',
    flexDirection: 'column',
  },

  '> div > span:last-child': {
    fontSize: 12,
    color: '#788596',
  },
});

const CloseConversation = styled('div')({
  display: 'none !important',
  position: 'relative',
  width: 40,
  height: '100%',

  [breakpoints.down(768.05)]: {
    display: 'block !important',
    height: 36,
    width: 36,
  },
});

const ConversationHeaderUsername = styled('span')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 16,
  color: getTextColor().LIGHT_GRAY.color,
  fontWeight: 600,
  lineHeight: '23px',
  letterSpacing: '-0.5px',
}));

const OnlineIcon = styled('div')(() => ({
  height: 12,
  width: 12,
  borderRadius: 7,
  backgroundColor: getColor().BRAND_1_ONLINE,
  display: 'inline-block',
}));

const HeaderDetailsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const LocationInfo = styled('span')(() => ({
  color: isDarkMode() ? '#AFB0B1' : '#778595',
  fontSize: 13,
  lineHeight: '18px',
  letterSpacing: '-0.3px',
}));

const FirstDateIncentiveText = styled('span')(() => ({
  ...getTextColor().LIGHT_GRAY,
  fontSize: 15,
  lineHeight: '20px',
  letterSpacing: '-0.3px',
  '& b': {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '21px',
    letterSpacing: '-0.3px',
  },
  [breakpoints.down(768.05)]: {
    lineHeight: '18px',
    '& b': {
      fontSize: 15,
      lineHeight: '18px',
    },
  },
}));

const Details = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',

  '> span': {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#263444',
  },
});

const UserCountry = styled('span')({
  [breakpoints.down(479.05)]: {
    display: 'none',
  },
});

const OfferAccepted = styled('span')(() => ({
  fontSize: 18,
  color: isDarkMode() ? '#71B5E4' : '#2490DE',
  fontWeight: 600,
  lineHeight: '23px',
  letterSpacing: '-0.5px',

  [breakpoints.down(768.05)]: {
    display: 'none',
  },
}));

const VideoCallModalStyles = {
  border: `1px solid ${isDarkMode() ? '#24A83A' : '#50B961'}`,
  borderRadius: '21px !important',
  padding: '9px 14px',
  [breakpoints.down(768.05)]: {
    padding: '6px 10px !important',
    borderRadius: '21px !important',
  },
  '&:focus': {
    background: 'transparent',
    color: 'unset',
  },
  '&:hover:enabled': {
    background: 'transparent',
  },
};

const ButtonModalStyles = {
  '&:focus': {
    background: 'transparent',
    color: `${getButtonTheme().darkReverse}`,
  },
  '&:hover': {
    border: 'none',
    background: 'transparent',
  },
};

const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = (): CustomStyledProps => ({
  borderRadius: 10,
  maxWidth: IsMobileViewPort ? 345 : 520,
  maxHeight: 575,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
  background: getBackgroundTheme().block2,
  border: `1px solid ${isDarkMode() ? '#848484' : '#FFFFFF'}`,
});

const menuActionButtonStyle = (): CustomStyledProps => ({
  ...getTextColor().DARK,
  padding: '20px 0',
  fontSize: 15,
  lineHeight: '19px',
  fontWeight: 600,
});

const ConversationHeader: React.FC<IConversationHeader> = ({ dataTestId, isConversationSevered = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();

  const { trackProfileView } = useMixPanel();

  const { isMobile } = useMediaBreakpoints();

  const currentConversation = useSelector((state: RootState) => state.mail.conversation);
  const authUser = useSelector((state: RootState) => state.profile);
  const videoInfo = useSelector((state: RootState) => state.settings.video_info);
  const iceServers = useSelector((state: RootState) => state.settings.iceServers);
  const userAuth = useSelector((state: RootState) => state.auth);
  const { reasons } = useSelector((state: RootState) => state.extprofile);

  const otherProfile = (currentConversation?.other_profile && currentConversation?.other_profile?.data) || {};
  const otherAccount = (otherProfile.account && otherProfile.account.data) || {};
  const isAdmin = currentConversation?.admin ?? false;
  const isOnline = otherProfile.online;
  const hashId = otherAccount.hash_id;
  const isLocked = currentConversation.locked;
  const isArchived = currentConversation.archived;
  const isLockedRequestRefund = currentConversation.locked_request_refund;
  const refundRequestedAt = currentConversation.refund_requested_at;
  const isAttractive = authUser && authUser.profile && authUser.profile.data.account_type === 'Attractive';
  const showRefundListItem = !isAttractive && !isLocked;
  const avatar = (otherProfile.avatar && otherProfile.avatar.data.urls) || {};
  const offerData = (currentConversation.offer && currentConversation.offer.data) || {};
  const currency = (offerData.currency_original && offerData.currency_original.data) || {};
  const currentPrice = `${currency.initial}${String.fromCharCode(currency.label_unicode)}${offerData.price_original}`;

  const memoizedOtherProfileAvatar = useMemo(() => renderAvatar(avatar, true), [avatar]);
  const memoizedProfileAvatar = useMemo(
    () => renderAvatar(authUser?.profile?.data?.avatar?.data?.urls),
    [authUser?.profile?.data?.avatar]
  );

  const [actionsOpen, setActionsOpen] = useState(false);
  const [showSeveredActions, setShowSeveredActions] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  useDisableBodyElementScroll(actionsOpen);

  const goToProfile = () => {
    trackProfileView({ Source: 'Conversations', User: hashId });
    navigate(`/profile/${hashId}`);
  };

  const handleCloseConversation = e => {
    e.stopPropagation();

    dispatch(closeConversation(navigate, location, params));
  };

  const handleToggleActionsModal = status => {
    setActionsOpen(status);
    setShowSeveredActions(currentConversation.deleted_reason === 'severed');
  };

  const formatRefundListItem = () => {
    if (refundRequestedAt) {
      return 'Refund requested';
    }

    if (!refundRequestedAt && isLockedRequestRefund) {
      return 'Conversation has been open for at least 30 days';
    }

    if (!refundRequestedAt && !isLockedRequestRefund) {
      return 'For refund requests, please contact customer service';
    }
  };

  const toggleReportModal = () => {
    handleToggleActionsModal(false);
    setShowReportModal(!showReportModal);
  };

  const handleReportUserClick = () => {
    if (isEmpty(reasons)) {
      dispatch(getReportReasons());
    }

    toggleReportModal();
  };

  if (isConversationSevered) {
    return (
      <StyledConversationHeader isConversationSevered>
        <ConversationHeaderBackBtnWrapper>
          <CloseConversation onClick={handleCloseConversation}>
            <ArrowLeft messageBack cssStyle={{ positon: undefined, margin: 0 }} />
          </CloseConversation>
          <span
            style={{
              color: getTextColor().DARK_4.color,
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '19px',
              letterSpacing: '-0.3px',
            }}
          >
            Back To Conversation
          </span>
          <Button onClick={() => handleToggleActionsModal(true)} style={{ border: 'none', marginLeft: 'auto' }}>
            <DotMenu />
          </Button>
        </ConversationHeaderBackBtnWrapper>
      </StyledConversationHeader>
    );
  }

  return (
    <StyledConversationHeader data-test-id={dataTestId}>
      <ConversationHeaderBackBtnWrapper>
        <CloseConversation onClick={handleCloseConversation}>
          <ArrowLeft messageBack cssStyle={{ positon: undefined, margin: 0 }} />
        </CloseConversation>
        <span
          style={{
            color: getTextColor().DARK_4.color,
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '19px',
            letterSpacing: '-0.3px',
          }}
        >
          Back To Conversation
        </span>
      </ConversationHeaderBackBtnWrapper>
      <ConversationHeaderUserInfoWrapper>
        <UserInfo onClick={() => !isAdmin && goToProfile()}>
          <Avatar
            src={isAdmin ? GENERAL_MESSAGE_AVATAR : memoizedOtherProfileAvatar}
            width={'100px !important'}
            data-test-id="conversation-header-avatar"
            customStyle={{ border: getBorders().PRIMARY, '& img': { height: 100, width: 100, borderRadius: 3 } }}
          />
          <div style={{ marginLeft: 10, flex: 1, height: '100%' }}>
            <ConversationHeaderUsername className="conversation-header-username">
              {isAdmin ? GENERAL_MESSAGE_USERNAME : otherAccount.username || ''}{' '}
              {!isAdmin && isOnline && <OnlineIcon />}
              {isAdmin && <commonIcons.check.badge />}
            </ConversationHeaderUsername>
            {!isAdmin && (
              <>
                <HeaderDetailsContainer>
                  <LocationInfo className="conversation-header-location">
                    {otherProfile.age} &bull; {otherProfile.city}
                    <UserCountry>, {otherProfile.country}</UserCountry>
                  </LocationInfo>
                </HeaderDetailsContainer>
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: 'auto', gap: 4 }}>
                  <OfferAccepted className="offer-accepted-text">Offer Accepted!</OfferAccepted>
                  <FirstDateIncentiveText className="first-date-incentive-text">
                    First date incentive: <b>{currentPrice}</b>
                  </FirstDateIncentiveText>
                </div>
              </>
            )}
          </div>
        </UserInfo>
        {!isAdmin && (
          <Details>
            {!isLocked && (
              <VideoCallModal
                customStyle={{ marginLeft: 5, ...VideoCallModalStyles }}
                profile={authUser}
                auth={userAuth}
                otherProfile={otherProfile}
                isVideoButtonEnabled={currentConversation.isVideoButtonEnabled}
                iceServers={iceServers}
                videoInfo={videoInfo}
                navigate={navigate}
                location={location}
                profileAvatar={memoizedProfileAvatar}
                otherProfileAvatar={memoizedOtherProfileAvatar}
              />
            )}
            <Button
              data-test-id="btn-dot-menu"
              isDefault={true}
              buttonType="button"
              onClick={() => handleToggleActionsModal(true)}
              customStyle={{ height: 34, border: 'none', ...ButtonModalStyles, marginLeft: isMobile ? 5 : 0 }}
            >
              <DotMenu color="#2D5979" />
            </Button>
          </Details>
        )}
      </ConversationHeaderUserInfoWrapper>

      {actionsOpen && (
        <ModalWithPattern
          dataTestID="ConversationActions"
          isModalOpen={actionsOpen}
          modalContentLabel="Conversation Actions"
          closeOnOverlayClick
          closeRequest={() => handleToggleActionsModal(false)}
          closeBtnClassName="conversation-actions-close"
          closeBtnDataTestId="conversation-actions-close"
          onCloseClick={() => handleToggleActionsModal(false)}
          customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
          customContentStyle={
            IsMobileViewPort
              ? mobileContentStyle()
              : { background: getBackgroundTheme().block2, borderWidth: 1, maxWidth: 450, maxHeight: 498 }
          }
        >
          <MenuList customStyle={{ width: '100%' }}>
            {!showSeveredActions && (
              <MenuListItem>
                <Button
                  fit
                  buttonType="chromeless"
                  onClick={handleReportUserClick}
                  customStyle={menuActionButtonStyle()}
                >
                  Report User
                </Button>
              </MenuListItem>
            )}
            {!showSeveredActions && (
              <MenuListItem>
                <Button
                  fit
                  buttonType="chromeless"
                  onClick={e => {
                    e.preventDefault();
                    setBlockModalOpen(true);
                    handleToggleActionsModal(false);
                  }}
                  customStyle={menuActionButtonStyle()}
                >
                  {otherAccount.is_blocked ? 'Unblock User' : 'Block User'}
                </Button>
              </MenuListItem>
            )}
            {!isLocked ? (
              <MenuListItem>
                <Button
                  fit
                  buttonType="chromeless"
                  to=""
                  onClick={() => {
                    dispatch(setToggleArchive(currentConversation.uid, 1, navigate, location, isArchived));
                    handleToggleActionsModal(false);
                  }}
                  customStyle={menuActionButtonStyle()}
                >
                  {isArchived ? 'Unarchive' : 'Archive'} This Conversation
                </Button>
              </MenuListItem>
            ) : (
              <MenuListItem>
                <Button
                  data-test-id="btn-cancel-offer"
                  fit
                  buttonType="chromeless"
                  to=""
                  onClick={() => {
                    dispatch(handleCancelOffer(otherAccount.hash_id, navigate, location, params, isMobile));
                    handleToggleActionsModal(false);

                    mixpanelTrackEvent({
                      eventName: EVENT_NAMES.OFFER_CANCELLED,
                      payload: {
                        eventProperties: {
                          Amount: offerData.price_original,
                          User: otherAccount.hash_id,
                          Source: 'conversation',
                        },
                      },
                      otherAccountHashId: offerData.initiator_hash_id,
                    });
                  }}
                  customStyle={menuActionButtonStyle()}
                >
                  Cancel this offer
                </Button>
              </MenuListItem>
            )}
            {showRefundListItem && (
              <MenuListItem customStyle={{ padding: '20px 0' }}>
                <label
                  style={{ ...getTextColor().DARK }}
                  dangerouslySetInnerHTML={{ __html: formatRefundListItem() }}
                ></label>
                <br />
                <br />
                {!isAttractive && !isLockedRequestRefund && !isLocked && (
                  <span style={{ ...getTextColor().DARK }}>
                    <small>
                      <i>You cannot have discussed contact info or date specifics. Subject to discretion.</i>
                    </small>
                  </span>
                )}
              </MenuListItem>
            )}
          </MenuList>
        </ModalWithPattern>
      )}

      {blockModalOpen && (
        <ConfirmationModal
          contentLabel="Block Modal"
          isOpen={blockModalOpen}
          description={`Are you sure you want to block ${otherAccount.username}?`}
          cancelText="Nevermind"
          confirmText="Block"
          onClose={() => setBlockModalOpen(false)}
          onConfirm={() => dispatch(setblockUser(hashId, otherAccount.username, navigate))}
        />
      )}

      {!isEmpty(reasons) && (
        <ReportModal
          showReportModal={showReportModal}
          toggleReportModal={toggleReportModal}
          hashId={hashId}
          username={otherAccount.username}
          avatar={memoizedOtherProfileAvatar}
          reasons={reasons}
        />
      )}
    </StyledConversationHeader>
  );
};

export default React.memo(ConversationHeader);
